import clsx from 'clsx'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import Delete from '@/assets/img/delete-icon.svg?react'
import DotsSvg from '@/assets/img/dots.svg'
import Edit from '@/assets/img/edit-pan.svg?react'
import AddIcon from '@/assets/img/ico-add.svg?react'
import ProfileIcon from '@/assets/img/ico-view-profile.svg?react'
import Button from '@/compoments/common/Button'
import Dropdown from '@/compoments/common/Dropdown'
import Pagination from '@/compoments/common/Pagination'
import { PillStatus } from '@/compoments/common/PillStatus'
import SelectBox from '@/compoments/common/SelectBox'
import PageLoader from '@/compoments/PageLoader'
import {
    useDeleteWorkGroupsMutation,
    useGetAllWorkGroupsQuery,
} from '@/services/tenant/workGroups'
import type { IWorkGroup } from '@/types/tenant/workGroup.types'
import { optionsTeamStatus } from '@/utils/selectsOptionsConst'
import UserPart from '@/compoments/common/UserPart'
import UsersGroup from '@/compoments/common/UsersGroup'
import DropdownList from '@/compoments/DropdownList'
import AddWorkGroupModal from '@/compoments/modals/AddWorkGroupModal'

export const AdminWorkGroupManagement = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const queryString = searchParams.toString()
    const { data, isLoading } = useGetAllWorkGroupsQuery(queryString)
    const [addWorkGroupModal, setAddWorkGroupModal] = useState(false)
    const [deleteWorkGroup] = useDeleteWorkGroupsMutation()
    const pageData = data?.data

    const [editWorkGroup, setEditWorkGroup] = useState<IWorkGroup | null>(null)
    const filterHandler = (
        name: string,
        value: string | number | undefined,
    ) => {
        setSearchParams((prev: URLSearchParams) => {
            prev.set('page', '1')
            if (value) {
                prev.set(name, String(value))
            } else {
                prev.delete(name)
            }
            return prev
        })
    }

    const editHandler = (group: IWorkGroup) => {
        setEditWorkGroup(group)
        setAddWorkGroupModal(true)
    }

    const deleteHandler = async (id: number) => {
        const { toast } = await import('react-toastify')
        try {
            const res = await deleteWorkGroup(id).unwrap()
            if (res?.success) {
                toast.success('Deleted successfully!')
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }

    if (isLoading) return <PageLoader />
    if (!data) return null
    return (
        <main className="content">
            <div className="px-container">
                <div className="table-group">
                    <div className="table-header flex items-center justify-between">
                        <div className="table-tools">
                            <SelectBox
                                options={optionsTeamStatus}
                                placeholder="Status"
                                onChange={(option) =>
                                    filterHandler('status', option?.value)
                                }
                                isClearable={true}
                            />
                        </div>

                        <Button
                            size="sm"
                            variant="primary-gradient"
                            onClick={() => setAddWorkGroupModal(true)}
                        >
                            <AddIcon />
                            <span className="text--xs">Create WorkGroup</span>
                        </Button>
                    </div>

                    <div className="table-body table-responsive p-0">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Dispatcher</th>
                                    <th>Executive</th>
                                    <th>Members</th>
                                    <th>Projects</th>
                                    <th style={{ width: '10px' }}>Status</th>
                                    <th style={{ width: '10px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageData && pageData.length > 0 ? (
                                    pageData.map((workGroup, index) => (
                                        <tr key={index}>
                                            <td>{workGroup.name}</td>
                                            <td>
                                                {workGroup.dispatcher ? (
                                                    <UserPart
                                                        src={
                                                            workGroup.dispatcher
                                                                .photo
                                                        }
                                                        name={
                                                            workGroup.dispatcher
                                                                .fullName
                                                        }
                                                        email={
                                                            workGroup.dispatcher
                                                                .email
                                                        }
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>
                                                {workGroup.executive ? (
                                                    <UserPart
                                                        src={
                                                            workGroup.executive
                                                                .photo
                                                        }
                                                        name={
                                                            workGroup.executive
                                                                .fullName
                                                        }
                                                        email={
                                                            workGroup.executive
                                                                .email
                                                        }
                                                    />
                                                ) : (
                                                    '-'
                                                )}
                                            </td>

                                            <td>
                                                <UsersGroup
                                                    list={workGroup.randomUsers.map(
                                                        (el) => ({
                                                            src: el.photo,
                                                        }),
                                                    )}
                                                    usersCount={
                                                        workGroup.totalUsers
                                                    }
                                                />
                                            </td>
                                            <td>
                                                {workGroup.projects} projects
                                            </td>
                                            <td>
                                                <PillStatus
                                                    status={workGroup.status}
                                                />
                                            </td>
                                            <td className="text-right sticky-end">
                                                <Dropdown
                                                    closeSelf={true}
                                                    dropdownClasses="flex flex-col"
                                                    button={
                                                        <span className="ico">
                                                            <img
                                                                src={DotsSvg}
                                                                alt=""
                                                            />
                                                        </span>
                                                    }
                                                    menuClassName="dropdown-md"
                                                    menu={
                                                        <DropdownList
                                                            list={[
                                                                {
                                                                    title: 'View Profile',
                                                                    icon: (
                                                                        <ProfileIcon />
                                                                    ),
                                                                    action: () => {
                                                                        navigate(
                                                                            `${workGroup.id}`,
                                                                        )
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Edit',
                                                                    icon: (
                                                                        <Edit />
                                                                    ),
                                                                    action: () =>
                                                                        editHandler(
                                                                            workGroup,
                                                                        ),
                                                                },
                                                                {
                                                                    title: 'Delete',
                                                                    icon: (
                                                                        <Delete />
                                                                    ),
                                                                    action: async () =>
                                                                        deleteHandler(
                                                                            workGroup.id,
                                                                        ),
                                                                },
                                                            ]}
                                                        />
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7}>
                                            <h4 className="text-center">
                                                Teams not found
                                            </h4>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <Pagination pagination={data?.pagination} />
            </div>
            {addWorkGroupModal && (
                <AddWorkGroupModal
                    isOpen={addWorkGroupModal}
                    onClose={() => setAddWorkGroupModal(false)}
                    selectedWorkGroup={editWorkGroup}
                    setSelectedWorkGroup={setEditWorkGroup}
                />
            )}
        </main>
    )
}
